import { useState } from "react";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import {
  BanIcon,
  EllipsisVerticalIcon,
  KeyRoundIcon,
  Loader2Icon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { toast } from "sonner";
import { timestampFromDate } from "@bufbuild/protobuf/wkt";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { ErrorState, FieldError } from "@/components/error-state";
import { EmptyState } from "@/components/page-header";
import { SecretRevealDialog } from "@/components/secret-reveal-dialog";
import { ToneBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdminService, type Account, type Token } from "@/gen/wabms/admin/v1/admin_pb";
import { adminKeys } from "@/lib/admin-queries";
import { errorMessage, errorTitle, unclaimedViolations, violationFor } from "@/lib/errors";
import {
  SCOPES,
  formatAbsolute,
  formatBoth,
  formatDay,
  formatRelative,
  tokenStatus,
  type Scope,
  type TokenStatus,
} from "@/lib/format";

const SCOPE_HELP: Record<Scope, string> = {
  "service:read": "Read GetService — limits, features and the target description.",
  "batches:read": "Read batches and message status. Enough for a status poller.",
  "batches:write": "Submit and cancel batches. This is the scope that can actually send.",
};

const STATUS_TONE: Record<TokenStatus, "success" | "neutral" | "warning"> = {
  active: "success",
  revoked: "neutral",
  expired: "warning",
};

export function TokensTab({ account }: { account: Account }) {
  const queryClient = useQueryClient();
  const tokensQuery = useQuery(AdminService.method.listTokens, { accountId: account.accountId });
  const tokens = tokensQuery.data?.tokens ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<Token | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<Token | undefined>(undefined);

  /**
   * The plaintext lives here and nowhere else: not in a zustand store, not in
   * the URL, and not in a query cache that outlives this component. The
   * CreateToken mutation is reset the moment its response has been read, so
   * the secret is not retained in the mutation cache either.
   */
  const [mintedSecret, setMintedSecret] = useState<string | undefined>(undefined);
  const [mintedToken, setMintedToken] = useState<Token | undefined>(undefined);

  const revokeToken = useMutation(AdminService.method.revokeToken);
  const deleteToken = useMutation(AdminService.method.deleteToken);

  function invalidateTokens(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: adminKeys.tokens() });
  }

  function handleCreated(token: Token | undefined, secret: string) {
    setMintedToken(token);
    setMintedSecret(secret);
    setCreateOpen(false);
    void invalidateTokens();
  }

  async function revoke(token: Token) {
    setPendingRevoke(undefined);
    const p = revokeToken.mutateAsync({ tokenId: token.tokenId }).then(async (res) => {
      await invalidateTokens();
      return res;
    });
    toast.promise(p, {
      loading: "Revoking…",
      success: `${token.name} revoked. Requests using it now fail with UNAUTHENTICATED.`,
      error: (err: unknown) => errorMessage(err),
    });
    await p.catch(() => undefined);
  }

  async function remove(token: Token) {
    setPendingDelete(undefined);
    const p = deleteToken.mutateAsync({ tokenId: token.tokenId }).then(async (res) => {
      await invalidateTokens();
      return res;
    });
    toast.promise(p, {
      loading: "Deleting…",
      success: `${token.name} deleted.`,
      error: (err: unknown) => errorMessage(err),
    });
    await p.catch(() => undefined);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Tokens are the CLIENT credentials for the BMI surface of this account. They are shown in
          plaintext once, at creation; the service only stores a hash.
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <PlusIcon /> Create token
        </Button>
      </div>

      {tokensQuery.isError ? (
        <ErrorState
          error={tokensQuery.error}
          title="Could not load tokens"
          onRetry={() => void tokensQuery.refetch()}
        />
      ) : tokensQuery.isPending ? (
        <Skeleton className="h-52 rounded-xl" />
      ) : tokens.length === 0 ? (
        <EmptyState
          icon={<KeyRoundIcon />}
          title="No tokens for this account"
          description="A CLIENT needs a token before it can submit batches."
          action={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <PlusIcon /> Create token
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Scopes</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((token) => {
                const status = tokenStatus(token);
                return (
                  <TableRow key={token.tokenId} className={status !== "active" ? "opacity-70" : ""}>
                    <TableCell className="font-medium">{token.name}</TableCell>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground">
                        {token.prefix}…
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {token.scopes.map((s) => (
                          <ToneBadge key={s} tone={s === "batches:write" ? "warning" : "info"}>
                            <span className="font-mono text-[10px]">{s}</span>
                          </ToneBadge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatAbsolute(token.createdAt)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {token.lastUsedAt ? formatRelative(token.lastUsedAt) : "never"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {token.expiresAt ? formatDay(token.expiresAt) : "never"}
                    </TableCell>
                    <TableCell>
                      <ToneBadge tone={STATUS_TONE[status]}>{status}</ToneBadge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${token.name}`}>
                            <EllipsisVerticalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            disabled={status !== "active"}
                            onSelect={() => setPendingRevoke(token)}
                          >
                            <BanIcon /> Revoke
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setPendingDelete(token)}
                          >
                            <Trash2Icon /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        accountId={account.accountId}
        onCreated={handleCreated}
      />

      <SecretRevealDialog
        open={mintedSecret !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            // Drop the plaintext as soon as the dialog is dismissed.
            setMintedSecret(undefined);
            setMintedToken(undefined);
          }
        }}
        title="Token created"
        description={`Give this value to the CLIENT as the Bearer token for ${account.label}.`}
        secret={mintedSecret ?? ""}
      >
        {mintedToken ? (
          <dl className="space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="w-24 text-xs text-muted-foreground">Name</dt>
              <dd>{mintedToken.name}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-24 text-xs text-muted-foreground">Scopes</dt>
              <dd className="font-mono text-xs">{mintedToken.scopes.join(", ")}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-24 text-xs text-muted-foreground">Expires</dt>
              <dd className="text-xs">
                {mintedToken.expiresAt ? formatBoth(mintedToken.expiresAt) : "never"}
              </dd>
            </div>
          </dl>
        ) : null}
      </SecretRevealDialog>

      <ConfirmDialog
        open={pendingRevoke !== undefined}
        onOpenChange={(open) => !open && setPendingRevoke(undefined)}
        title={`Revoke ${pendingRevoke?.name ?? "token"}?`}
        confirmLabel="Revoke token"
        destructive
        description={
          <p>
            The token stops working immediately, but the row stays in the table so you can see when
            it was last used. Delete it separately once you no longer need the audit trail.
          </p>
        }
        onConfirm={() => pendingRevoke && void revoke(pendingRevoke)}
      />

      <ConfirmDialog
        open={pendingDelete !== undefined}
        onOpenChange={(open) => !open && setPendingDelete(undefined)}
        title={`Delete ${pendingDelete?.name ?? "token"}?`}
        confirmLabel="Delete token"
        destructive
        description={
          <p>
            Removes the row entirely, including its last-used record. If the token has not been
            revoked yet, deleting it also stops it working.
          </p>
        }
        onConfirm={() => pendingDelete && void remove(pendingDelete)}
      />
    </div>
  );
}

function CreateTokenDialog({
  open,
  onOpenChange,
  accountId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  onCreated: (token: Token | undefined, secret: string) => void;
}) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Scope[]>(["service:read", "batches:read"]);
  const [expiry, setExpiry] = useState("");
  const [error, setError] = useState<unknown>(undefined);

  const createToken = useMutation(AdminService.method.createToken);

  function toggle(scope: Scope, checked: boolean) {
    setScopes((prev) => (checked ? [...new Set([...prev, scope])] : prev.filter((s) => s !== scope)));
  }

  async function submit() {
    if (name.trim().length === 0 || scopes.length === 0) return;
    setError(undefined);
    try {
      const res = await createToken.mutateAsync({
        accountId,
        name: name.trim(),
        // Preserve the canonical order rather than click order.
        scopes: SCOPES.filter((s) => scopes.includes(s)),
        expiresAt: expiry ? timestampFromDate(new Date(`${expiry}T23:59:59`)) : undefined,
      });
      const secret = res.secret;
      const token = res.token;
      // Clear the mutation state immediately so the plaintext does not sit in
      // the mutation cache once the dialog is gone.
      createToken.reset();
      setName("");
      setScopes(["service:read", "batches:read"]);
      setExpiry("");
      onCreated(token, secret);
    } catch (err) {
      setError(err);
    }
  }

  const today = new Date().toISOString().slice(0, 10);
  const nameViolation = violationFor(error, "name");
  const scopesViolation = violationFor(error, "scopes");
  const expiryViolation = violationFor(error, "expires_at");
  const otherViolations = unclaimedViolations(error, ["name", "scopes", "expires_at"]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setError(undefined);
          createToken.reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create access token</DialogTitle>
          <DialogDescription>
            Scope it to exactly what the CLIENT needs. Only <code>batches:write</code> can cause a
            message to be sent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="token-name">Name</Label>
          <Input
            id="token-name"
            placeholder="campaign-sender (prod)"
            value={name}
            aria-invalid={nameViolation !== undefined}
            onChange={(e) => setName(e.target.value)}
          />
          <FieldError message={nameViolation} />
          <p className="text-xs text-muted-foreground">
            Shown in the table and in audit logs. Name it after the system that holds it.
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Scopes</legend>
          <div className="space-y-2 rounded-lg border p-3">
            {SCOPES.map((scope) => (
              <label key={scope} className="flex cursor-pointer items-start gap-2.5">
                <Checkbox
                  className="mt-0.5"
                  checked={scopes.includes(scope)}
                  onCheckedChange={(c) => toggle(scope, c === true)}
                />
                <span className="space-y-0.5">
                  <span className="block font-mono text-xs">{scope}</span>
                  <span className="block text-xs text-muted-foreground">{SCOPE_HELP[scope]}</span>
                </span>
              </label>
            ))}
          </div>
          {scopes.length === 0 ? (
            <p className="text-xs text-destructive">Pick at least one scope.</p>
          ) : (
            <FieldError message={scopesViolation} />
          )}
        </fieldset>

        <div className="space-y-2">
          <Label htmlFor="token-expiry">Expiry (optional)</Label>
          <Input
            id="token-expiry"
            type="date"
            min={today}
            value={expiry}
            aria-invalid={expiryViolation !== undefined}
            onChange={(e) => setExpiry(e.target.value)}
          />
          <FieldError message={expiryViolation} />
          <p className="text-xs text-muted-foreground">
            Leave empty for a token that never expires. Prefer an expiry for anything handed to a
            third party.
          </p>
        </div>

        {error !== undefined &&
        nameViolation === undefined &&
        scopesViolation === undefined &&
        expiryViolation === undefined ? (
          <ErrorState error={error} title={errorTitle(error, "Could not create the token")} />
        ) : otherViolations.length > 0 ? (
          <ErrorState error={error} title="Some fields were rejected" />
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={createToken.isPending || name.trim().length === 0 || scopes.length === 0}
            onClick={() => void submit()}
          >
            {createToken.isPending ? <Loader2Icon className="animate-spin" /> : null}
            Create token
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
